import React, { useState } from "react";
import {
  View,
  Image,
  ScrollView,
  Pressable,
  Modal,
  StatusBar,
  TouchableWithoutFeedback,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { X, ChevronLeft, ChevronRight } from "lucide-react-native";
import type { Clinic } from "@louise/clinics/clinics.types";
import {
  getClinicFacilityImages,
  getClinicDisplayImageUrl,
  type ClinicDisplayImage,
} from "./clinicImagePolicy";

import { usePressCapture } from "@src/packages/analytics/usePressCapture";
import { IconButton } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { semanticColors, withAlpha } from "@louise/theme/tokens";
function getProviderImageUrl(
  image: ReturnType<typeof getClinicFacilityImages>[number] | undefined,
) {
  return image ? getClinicDisplayImageUrl(image) : null;
}

type Props = {
  clinic: Clinic;
};

export default function ClinicImageGallery({ clinic }: Props) {
  const capturePress = usePressCapture("ClinicImageGallery");
  const t = useTranslations();
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const insets = useSafeAreaInsets();

  const images = getClinicFacilityImages(clinic.images as ClinicDisplayImage[]);

  if (images.length === 0) return null;
  const previewUrl =
    previewIndex !== null ? getProviderImageUrl(images[previewIndex]) : null;

  return (
    <>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ gap: 12, paddingHorizontal: 4 }}
        decelerationRate="fast"
      >
        {images.map((image, index) => {
          const imageUrl = getProviderImageUrl(image);
          return (
            <Pressable
              key={`${image.url}-${index}`}
              onPress={() => {
                // Image thumbnail with no visible copy to name it.
                capturePress("open_clinic_image");
                setPreviewIndex(index);
              }}
              style={{
                width: 280,
                height: 190,
                borderRadius: 20,
                overflow: "hidden",
                borderWidth: 1,
                borderColor: semanticColors.border.glass,
              }}
            >
              {imageUrl ? (
                <Image
                  source={{ uri: imageUrl }}
                  style={{ width: "100%", height: "100%" }}
                  resizeMode="cover"
                />
              ) : (
                <View
                  style={{
                    width: "100%",
                    height: "100%",
                    backgroundColor: withAlpha(
                      semanticColors.surface.default,
                      0.15,
                    ),
                  }}
                />
              )}
            </Pressable>
          );
        })}
      </ScrollView>

      {/* Full-screen preview modal */}
      <Modal
        visible={previewIndex !== null}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => setPreviewIndex(null)}
      >
        <StatusBar backgroundColor="black" barStyle="light-content" />
        <TouchableWithoutFeedback onPress={() => setPreviewIndex(null)}>
          <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.95)" }}>
            {previewUrl && (
              <Image
                source={{ uri: previewUrl }}
                style={{ flex: 1 }}
                resizeMode="contain"
              />
            )}
            {/* Close button */}
            <IconButton
              accessibilityLabel={t(
                "access.clinic_detail.photo_gallery.close_photo",
              )}
              action="close_clinic_image"
              onPress={() => setPreviewIndex(null)}
              size={36}
              backgroundColor={semanticColors.onDark.fill.subtle}
              pressedBackgroundColor={semanticColors.onDark.fill.strong}
              style={{ position: "absolute", top: insets.top + 12, right: 16 }}
            >
              <X size={20} color={semanticColors.onDark.text.primary} />
            </IconButton>

            {/* Prev / dots / next */}
            {images.length > 1 && previewIndex !== null && (
              <View
                style={{
                  position: "absolute",
                  bottom: insets.bottom + 24,
                  left: 0,
                  right: 0,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 16,
                }}
              >
                <IconButton
                  accessibilityLabel={t(
                    "access.clinic_detail.photo_gallery.previous_photo",
                  )}
                  action="previous_clinic_image"
                  onPress={(e) => {
                    e.stopPropagation();
                    setPreviewIndex((i) => (i !== null && i > 0 ? i - 1 : i));
                  }}
                  disabled={previewIndex === 0}
                  disabledOpacity={0.2}
                  size="sm"
                >
                  <ChevronLeft
                    size={20}
                    color={semanticColors.onDark.text.primary}
                  />
                </IconButton>

                <View
                  style={{
                    flexDirection: "row",
                    gap: 6,
                    alignItems: "center",
                  }}
                >
                  {images.map((_, i) => (
                    <Pressable
                      key={i}
                      onPress={(e) => {
                        e.stopPropagation();
                        // Dot indicator with no visible copy to name it.
                        capturePress("jump_to_clinic_image");
                        setPreviewIndex(i);
                      }}
                    >
                      <View
                        style={{
                          width: i === previewIndex ? 20 : 6,
                          height: 6,
                          borderRadius: 3,
                          backgroundColor:
                            i === previewIndex
                              ? semanticColors.onDark.text.primary
                              : semanticColors.onDark.text.tertiary,
                        }}
                      />
                    </Pressable>
                  ))}
                </View>

                <IconButton
                  accessibilityLabel={t(
                    "access.clinic_detail.photo_gallery.next_photo",
                  )}
                  action="next_clinic_image"
                  onPress={(e) => {
                    e.stopPropagation();
                    setPreviewIndex((i) =>
                      i !== null && i < images.length - 1 ? i + 1 : i,
                    );
                  }}
                  disabled={previewIndex === images.length - 1}
                  disabledOpacity={0.2}
                  size="sm"
                >
                  <ChevronRight
                    size={20}
                    color={semanticColors.onDark.text.primary}
                  />
                </IconButton>
              </View>
            )}
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </>
  );
}
